import { Form } from '@formio/react'
import { Alert, Button, CircularProgress, Icon, IconButton, List, ListItem, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import { track, useContent, useInAppSneezeGuard, useBrowserSneezeGuard, useGetIdCheckUrl } from '../../logic'
import { PortalSigner, SaveInProgressFormsEnum } from '../../types'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'

import "bootstrap/dist/css/bootstrap.css"
import "formiojs/dist/formio.full.min.css"
import { Box } from '@mui/system'
import { useLocation, useParams } from 'react-router-dom'
import { ErrorDialog } from './dialogs/ErrorDialog'
import { LoadingSigningStatuses } from './LoadingSignatureIframe'
import { PortalSignature } from './PortalSignature'
import { NotificationPagination } from './NotificationPagination'
import { toTitleCase } from '../../utils'
import { LoadingButton } from '@mui/lab'
import { usePageVisibility } from './usePageVisibility'
import { useFileUpload } from './useFileUpload'
import { usePrevious } from './usePrevious'
import { useNotificationsDialog } from './usePollNotifications'
import { AccountVerificationTask } from './tasks/accountVerificationTask/AccountVerificationTask'
import { UploadDialog } from './UploadDialog'
import { CancelTaskDialog } from './dialogs/CancelTaskDialog'
import { ResetTaskDialog } from './dialogs/ResetTaskDialog'
import { CompleteTaskDialog } from './dialogs/CompleteTaskDialog'
import { FormSubmitErrorDialog } from './dialogs/FormSubmitErrorDialog'
import { EarnestMoneyTask } from './tasks/earnestMoneyTask/EarnestMoneyTask'
import { DownloadUploadsComponent } from '../DownloadUploadsComponent'
import { useFormRetrieve, useGetNotification, useGetNotificationStatusesV2, useGetOrder } from '../../logic/useApiQuery'
import { useUserResetTask, useUserCancelTask, useUserCompleteTask, useFormSubmit, useFormSave } from '../../logic/useApiMutation'
import { useOptimisticUpdateNotificationAndOrderNotification } from '../../logic/optimisticUpdates'
import { useInterval } from '../../logic/useInterval'
import { useOrganization } from '../OrganizationProvider'
import { apiRoutes } from '../../logic/apiRoutes'
import { useQueryClient } from '@tanstack/react-query'
import { CashToCloseTask } from './tasks/cashToCloseTask/CashToCloseTask'
import { showUserCancelFunction } from '../../utils/showUserCancelFunction'
import { useAppTheme } from '../../logic/useAppTheme'
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder'

const reOpenEsigningMessage = 'View your e-signing'
const notificationStatusesPollingDelay = 2 * 1000;
const defaultNotificationStatusesPollMax = 8
const multiSignerNotificationStatusesPollMax = 4


export function PortalTaskComponent() {

  const { closingId } = useParams()
  const { search } = useLocation()

  const theme = useAppTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryColor = theme.palette.primary.main
  const secondaryColor = theme.palette.secondary.main

  const org = useOrganization()
  const { SaveInProgressForms } = org
  const saveInProgressIsEnabled = SaveInProgressForms === SaveInProgressFormsEnum.Enabled

  const { data: notification, isFetching: notificationIsFetching } = useGetNotification()
  const form = notification?.Form
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { TaskText, WiringInstructionsText } = useContent()
  const orderKey = globalOrderData?.Order?.CompanyIdOrderIdHash ?? '' //Temporary fix until we remove order from PortalTaskComponent and encapsulate order logic in the individual components
  const formKey = form?.FormId ?? ''
  const isSigningTask = notification?.Type === 'SigningTask'
  const hasSigning = form?.HasSignature || isSigningTask
  const hasDataSubmissionStep = notification?.HasDataSubmissionStep
  const isUploadTask = notification?.Type === 'UploadTask'
  const isIdCheckTask = notification?.Type === 'IdCheckTask'
  const isWiringInstructionsTask = notification?.Type === 'WiringInstructionsTask'
  const isAccountVerificationTask = notification?.Type === 'AccountValidationTask'
  const isEarnestMoneyTask = notification?.Type === 'EarnestMoneyTask'
  const isCashToCloseTask = notification?.Type === 'CashToCloseTask'
  const isFincenTask = notification?.Type === 'FincenFormTask'

  const shouldSaveInProgressForms = !!form && (saveInProgressIsEnabled || isFincenTask) && !notificationIsFetching

  const WiringInstructionsTaskCompleteButtonText = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order?.WiringInstructions?.TaskCompleteButtonText) || '';

  const [uploadTaskDialogOpen, setUploadTaskDialogOpen] = useState(false);
  const { uploadInput, fileUploadBody, fileUploadActions, resetFileUploadHook, isComplete } = useFileUpload({ notificationId: notification?.NotificationId })


  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const [formData, setFormData] = useState({})
  const [finalForm, setFinalForm] = useState<any>(null)
  const latestSavedFormDataJsonString = useRef('')
  const isFormReady = useRef(false)
  const isInitialChange = useRef(true)
  const latestFormDataJson = useRef<Record<string, any> | null>(null)
  const [sneezeGuardShouldPrompt, setSneezeGuardShouldPrompt] = useState(false)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [additionalSigners, setAdditionalSigners] = useState([] as PortalSigner[])
  const [notificationStatusesPollMax, setNotificationStatusesPollMax] = useState(defaultNotificationStatusesPollMax);

  // Don't auto open signing for e-sign anything signings so we can show the description
  const [signatureOpen, setSignatureOpen] = useState(!isSigningTask);

  const [userCancelOpen, setUserCancelOpen] = useState(false);
  const [userResetOpen, setUserResetOpen] = useState(false);
  const [userCompleteOpen, setUserCompleteOpen] = useState(false);
  const [formSubmitErrorOpen, setFormSubmitErrorOpen] = useState(false);

  const [idCheckUrl, setIdCheckUrl] = useState('')
  const queryClient = useQueryClient()

  const { refetch: refreshNotificationStatuses, data: notificationStatusesResponse, isFetching: isLoadingNotificationStatuses, error: notificationStatusesError } = useGetNotificationStatusesV2(orderKey, notification?.NotificationId || '', search, {
    enabled: false, //Disable it so it will only fetch when called with refetch.  This current design is not optimal, but it can be improved later.
    onSuccess: (updatedData) => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { ...updatedData } })
      }
    }
  })
  const [lastPoll, setLastPoll] = useState(0)
  const [pollCount, setPollCount] = useState(0)
  const [isPolling, setIsPolling] = useState(false)

  const { mutate: mutateFormSubmit, error: formSubmitError, data: userFormSubmitSuccess } = useFormSubmit(orderKey, formKey, search, {
    onSuccess: () => {
      if (!notification) return
      const isComplete = !hasSigning
      optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsComplete: isComplete, IsFormComplete: true } })
      if (isComplete) {
        maybeSetPollNotificationsDialogOpen()
      } else {
        setSignatureOpen(true)
      }
    }
  })

  const { mutate: mutateFormSave } = useFormSave(orderKey, notification?.NotificationId || '', formKey, search, {
    onSuccess: () => {
      setFormSaveStatus('saved')
      formSaveStatusTimestamp.current = Date.now()
    },
    //TODO: Figure out what to do when save errors.  Possibly have a little text box saying save failed.
    onError: () => {
      setFormSaveStatus('idle')
    }
  })
  const { data: formRetrieveData, isLoading: formRetrieveLoading, isFetching: formRetrieveFetching } = useFormRetrieve(orderKey, notification?.NotificationId || '', formKey, search, {
    cacheTime: 0,
    staleTime: 0,
    enabled: shouldSaveInProgressForms,
    onSuccess: (data: any) => {
      if (data) {
        const formJson = JSON.stringify(data)
        if (form?.Config && data?.schema) {
          setFinalForm(data.schema)
        }
        latestFormDataJson.current = data;
        latestSavedFormDataJsonString.current = formJson;
      }
    },
    retry: false
  })

  const inProgressFormRetrievalLoading = shouldSaveInProgressForms ? formRetrieveLoading : false;

  const { mutate: mutateUserCancel, isLoading: userCancelLoading, error: userCancelError } = useUserCancelTask(orderKey, notification?.NotificationId || '', search, {
    onSuccess: () => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsCancelled: true, IsComplete: true } })
        setUserCompletedSigning(false)
      }
    }
  })

  const { mutate: mutateUserResetTask, isLoading: userResetLoading, error: userResetError } = useUserResetTask(orderKey, notification?.NotificationId || '', search, {
    onMutate: () => {
      if (notification?.NotificationId && form) {
        latestFormDataJson.current = null
        latestSavedFormDataJsonString.current = ''
        isInitialChange.current = true
        isFormReady.current = false
        // Refetch the saved form data
        if (shouldSaveInProgressForms) {
          queryClient.invalidateQueries([apiRoutes.formRetrieve(orderKey, notification.NotificationId, formKey, search)])
        }
      }
    },
    onSuccess: () => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({
          orderKey, notificationId: notification.NotificationId, search, patch:
            { IsFormComplete: false, IsSigningCancelled: false, IsSigningComplete: false, IsCancelled: false, IsComplete: false }
        })
        setUserCompletedSigning(false)
        setUserResetOpen(false)

      }
    }
  })

  const { isLoading: userCompleteLoading, error: userCompleteError, mutate: mutateUserCompleteTask } = useUserCompleteTask(orderKey, notification?.NotificationId || '', search, {
    onSuccess: () => {
      if (notification?.NotificationId) {
        maybeSetPollNotificationsDialogOpen()
        optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsComplete: true } })
      }
    }
  })

  //Non tanstack because I do not understand what resetGetidCheckUrl does nor the useEffect that its used in
  const { refresh: refreshGetIdCheckUrl, loading: getIdCheckUrlLoading, error: getIdCheckUrlError, data: getIdCheckUrlSuccess, resetState: resetGetIdCheckUrl } = useGetIdCheckUrl(orderKey, notification?.NotificationId || '', search)

  const [userCompletedSigning, setUserCompletedSigning] = useState(false)
  const [openedIdCheckUrl, setOpenedIdCheckUrl] = useState(false)

  const isVisible = usePageVisibility();
  const prevIsVisible = usePrevious(isVisible);

  const [formSaveStatus, setFormSaveStatus] = useState<'idle' | 'saving' | 'saved'>('saved')
  const formSaveStatusTimestamp = useRef<number>(0)


  const { pollNotificationsDialog, handleOpen: setPollNotificationsDialogOpen } = useNotificationsDialog()

  const showForm = form && !isPolling && !notification?.IsFormComplete && !notification?.IsComplete && !notificationIsFetching && !inProgressFormRetrievalLoading


  const handleUploadClick = () => {
    if (isComplete) {
      resetFileUploadHook()
    }

    setUploadTaskDialogOpen(true)
  }

  const handleIdCheckClick = () => {
    resetPolling()
    refreshGetIdCheckUrl()
  }

  const onUploadTaskDialogClose = (e: any) => {
    e.preventDefault()
    setUploadTaskDialogOpen(false)
    setUserCompleteOpen(true)
  }

  const maybeSetPollNotificationsDialogOpen = () => {
    if (hasDataSubmissionStep) {
      setPollNotificationsDialogOpen()
    }
  }
  let prompt = 'You will lose changes to your form. Are you sure you wish to leave?';
  if (isFormModalOpen) {
    prompt = 'It looks like you’re still editing something! Please save your work or close the entry window before moving to another page.'
  }
  const shouldBlockNavigation = sneezeGuardShouldPrompt || isFormModalOpen
  useInAppSneezeGuard(prompt, shouldBlockNavigation)
  useBrowserSneezeGuard(shouldBlockNavigation)

  useEffect(() => {
    if (getIdCheckUrlSuccess && !isComplete && !openedIdCheckUrl) {
      // add idverse url to not warn about leaving the portal?
      // TODO: check on pop up blockers?
      window.open(getIdCheckUrlSuccess.url, '_blank')

      setIdCheckUrl(getIdCheckUrlSuccess.url ?? '')

      setOpenedIdCheckUrl(true)
      resetGetIdCheckUrl({ loading: false })
    } else if (!isComplete && openedIdCheckUrl && !prevIsVisible && isVisible) {
      // TODO: think about polling without blocking the rest of the ui
      // would we like this in general? if not always, we could break up this component
      setIsPolling(true)
      setOpenedIdCheckUrl(false)
    }
  }, [getIdCheckUrlSuccess, resetGetIdCheckUrl, openedIdCheckUrl, isComplete, isVisible, prevIsVisible])
  //
  // Toggle form submit error dialog
  useEffect(() => {
    setFormSubmitErrorOpen(!!formSubmitError)
  }, [formSubmitError])

  function updateAdditionalSigners(data: any) {
    let unauthedSigners: PortalSigner[] = []

    for (const [key, val] of Object.entries(data)) {
      if (key.startsWith('signerNames')) {
        const moreSigners = (val as any).filter((signer: PortalSigner) => signer.email)
        unauthedSigners = [...unauthedSigners, ...moreSigners]
      }
    }

    if (unauthedSigners.length) {
      setNotificationStatusesPollMax(multiSignerNotificationStatusesPollMax)
    }

    setAdditionalSigners(unauthedSigners)
  }

  //form saving.  save form every 15 seconds if there are changes
  useInterval(() => {
    if (isFormReady.current && shouldSaveInProgressForms) {
      const latestJsonString = latestFormDataJson.current ? JSON.stringify(latestFormDataJson.current) : null
      if (latestJsonString && latestJsonString !== latestSavedFormDataJsonString.current) {
        mutateFormSave(latestFormDataJson.current)
        latestSavedFormDataJsonString.current = latestJsonString
        setSneezeGuardShouldPrompt(false)
      }
    }
  }, 15000)

  // form submitted
  const onSubmit = (event: any, form: any) => {
    const payload = {
      ...event,
      notificationId: notification?.NotificationId,
      schema: event?.schema,
    }

    const title = form?.DisplayName || notification?.Title || 'Form'
    payload.title = title
    // TODO: real description?
    payload.description = title

    // NOTE: we moved title and description out of data to prevent collisions
    // they were left in the nested structure for backward compatibility (e.g. tokens could reference them for data submission steps)
    payload.data = {
      title,
      description: title,
      ...payload.data
    }

    updateAdditionalSigners(payload.data)
    setFormData(payload)
    mutateFormSubmit({ formData: payload, notificationId: notification?.NotificationId })
    if (shouldSaveInProgressForms) {
      mutateFormSave(latestFormDataJson.current)
    }
    setSneezeGuardShouldPrompt(false)
    latestSavedFormDataJsonString.current = JSON.stringify(latestFormDataJson.current)
  }

  function onUserCancelTask() {
    setIsPolling(false)
    mutateUserCancel(undefined)
    setUserCancelOpen(false)
  }

  function onUserResetTask() {
    setIsPolling(false)
    mutateUserResetTask(undefined)
    if (notification?.NotificationId) {

      setUserCompletedSigning(false)

      setUserResetOpen(false)
    }
  }

  function onUserCompleteTask() {
    setIsPolling(false)
    mutateUserCompleteTask(undefined)
    setUserCompleteOpen(false)
  }

  useEffect(() => {
    return () => {
      // force close modals on unmount
      const formioModals = document.querySelectorAll('.formio-dialog, .formio-modal');
      formioModals.forEach(el => el.remove());
      document.body.classList.remove('modal-open');
      // cleanup polling on unmount
      setIsPolling(false)
    }
  }, [])

  useEffect(() => {
    if (!hasSigning && !isIdCheckTask) {
      resetPolling()

      // Look for signing specific events instead of only isComplete, otherwise task can
      // look complete depending on the timing of creating the formComplete event
      // and the needsSigning event. This will allow polling to continue longer and
      // all events to be created
    } else if (hasSigning && (notificationStatusesResponse?.IsSigningComplete || notificationStatusesResponse?.IsSigningCancelled)) {
      resetPolling()
      maybeSetPollNotificationsDialogOpen()
      // this would only apply if they submitted the form and then came back later to complete the signing
    } else if (isIdCheckTask && notificationStatusesResponse?.IsComplete) {
      resetPolling()
      maybeSetPollNotificationsDialogOpen()
    } else if (isPolling && !isLoadingNotificationStatuses) {
      pollNotificationStatuses()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingNotificationStatuses, notificationStatusesResponse, isPolling]);


  // attempt to poll for notificationStatuses event
  function pollNotificationStatuses() {
    const now = Date.now()
    const elapsed = now - lastPoll
    const remainingDelay = notificationStatusesPollingDelay - elapsed
    const pollingMaxReached = pollCount >= notificationStatusesPollMax

    if (!isPolling) {
      return
    } else if (pollingMaxReached) {
      resetPolling()
    } else {
      setTimeout(() => {
        refreshNotificationStatuses()
        setLastPoll(Date.now())
        setPollCount((prevCount) => prevCount + 1)
      }, Math.max(remainingDelay, 0))
    }
  }

  function onSigningClose() {
    const isSingleSigner = userFormSubmitSuccess && additionalSigners?.length === 0
    const isMultiSigner = userFormSubmitSuccess && additionalSigners?.length > 0

    if (isSingleSigner && userCompletedSigning && notification?.NotificationId) {
      maybeSetPollNotificationsDialogOpen()
      optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsComplete: true, IsFormComplete: true, IsSigningComplete: true } })

    } else if (isMultiSigner && userCompletedSigning) {
      maybeSetPollNotificationsDialogOpen()
    } else {
      setIsPolling(true)
    }
  }

  function resetPolling() {
    setIsPolling(false)
    setPollCount(0)
    setLastPoll(0)
  }

  useEffect(() => {
    let observer: MutationObserver | undefined

    if (showForm) {
      observer = new MutationObserver(() => {
        const hasModal = document.querySelector('.formio-dialog') !== null
        setIsFormModalOpen(hasModal)
      })

      observer.observe(document.body, { childList: true })
    } else {
      // Ensure sneeze guard state is cleared when the form is not shown  
      setIsFormModalOpen(false)
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }
      // Cleanup also clears any lingering modal state  
      setIsFormModalOpen(false)
    }
  }, [showForm])

  // error handling
  if (formSubmitError || userCompleteError || userCancelError || notificationStatusesError || userResetError || getIdCheckUrlError) {
    let err
    let label

    if (formSubmitError) {
      err = formSubmitError
      label = 'useFormSubmit'
    } else if (userCompleteError) {
      err = userCompleteError
      label = 'useUserCompleteTask'
    } else if (userCancelError) {
      err = userCancelError
      label = 'useUserCancelTask'
    } else if (notificationStatusesError) {
      err = notificationStatusesError
      label = 'useGetNotificationStatuses'
    } else if (userResetError) {
      err = userResetError
      label = 'useUserResetTask'
    } else if (getIdCheckUrlError) {
      err = getIdCheckUrlError
      label = 'useGetIdCheckUrl'
    }

    // log all to trackjs
    track('error', label, {}, {
      message: JSON.stringify(err),
      component: 'PortalTaskComponent',
    })

    // Alert errors from user actions, not polling errors. formSubmitError handled
    // separately to help avoid losing form data.
    if (userCompleteError || userCancelError || userResetError || getIdCheckUrlError) {
      return <ErrorDialog error='There was an error processing your request.' tryAgainText='Reload and try again' />
    }
  }

  function getAdditionalSignersText() {
    const multiple = additionalSigners.length > 1
    return `${multiple ? 'Emails have' : 'An email has'} been sent to the following ${multiple ? 'people' : 'person'} to facilitate their e-signing${multiple ? 's' : ''}:`
  }

  const submission = latestFormDataJson.current ? { data: latestFormDataJson.current } : formRetrieveData ? { data: formRetrieveData } : undefined
  const showCompleteMessage = !isPolling && notification?.IsComplete && !notification?.IsCancelled
  const showCancelledMessage = !isPolling && notification?.IsComplete && notification?.IsCancelled
  const showLinkToDownloadSigningDocs = !isPolling && (hasSigning) && !notification?.IsCancelled && notification?.IsComplete
  const showLinkToDownloadForm = !isPolling && !notification?.IsCancelled && notification?.IsComplete && notification?.IsFormComplete && ((!hasSigning) || (form?.HasSignature && notification?.IsSigningCancelled))
  const showLinkToDownloadUploadTaskDocs = isUploadTask && !isPolling && !notification?.IsCancelled && notification?.IsComplete
  const showSigningIncompleteDetails = !isPolling && !notification?.IsComplete && ((notification?.IsFormComplete && form?.HasSignature) || isSigningTask)
  const showSigningDialog = !notification?.IsComplete && ((notification?.IsFormComplete && form?.HasSignature && !notification?.IsSigningComplete) || (isSigningTask && !notification?.IsSigningComplete))
  const showNotificationPagination = notification?.IsFormComplete || notification?.IsComplete
  const showPrimaryFormLoadingSpinner = form && !showForm && !isPolling && !notification?.IsFormComplete && !notification?.IsComplete && !notification?.IsCancelled

  const showReopenYourSigningButton = isPolling && hasSigning
  const showUploadButton = isUploadTask && !notification?.IsComplete
  const showIdCheckButton = isIdCheckTask && !notification?.IsComplete && !isPolling

  // Hiding user complete for now.
  const showUserCompleteButton = false && isUploadTask && !notification?.IsComplete
  const showUserCancelButton = notification && showUserCancelFunction(notification, org)
  const showUserResetButton = !isIdCheckTask && !isSigningTask && !isAccountVerificationTask && !isPolling && !isEarnestMoneyTask && (notification?.IsComplete || notification?.IsFormComplete || notification?.IsCancelled) && !isCashToCloseTask

  function handleFormSave() {
    mutateFormSave(latestFormDataJson.current)
    setFormSaveStatus('saving')
    setSneezeGuardShouldPrompt(false)
  }

  function handleFormChange({ data }: { data: object }) {
    //This is ran even after the onSubmit
    if (!isFormReady.current) return

    if (isInitialChange.current) {
      const initialDataJsonString = JSON.stringify(data)
      latestSavedFormDataJsonString.current = initialDataJsonString
      isInitialChange.current = false
      return // Don't check for changes on the first event
    }

    latestFormDataJson.current = data
    const latestJsonString = JSON.stringify(data)
    const hasChanges = latestJsonString !== latestSavedFormDataJsonString.current
    const shouldPrompt = hasChanges && !notification?.IsFormComplete && !notification?.IsComplete
    setSneezeGuardShouldPrompt(shouldPrompt)

    if (shouldPrompt) {
      const now = Date.now()
      const timeElapsedSinceSave = now - formSaveStatusTimestamp.current
      // If we're within the 2.5-second window after saving, don't change the status
      if (timeElapsedSinceSave > 2500) {
        setFormSaveStatus('idle')
      }
    }
  }

  if (!globalOrderData) {
    return null
  }

  return (
    <>
      {/* polling for notificationStatuses */}
      {isPolling && <>
        <LoadingSigningStatuses />
      </>}

      {showReopenYourSigningButton && <>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Button
            variant="outlined"
            sx={{
              minWidth: "250px",
              alignSelf: "center",
              mt: 4,
              borderRadius: "40px",
            }}
            onClick={() => {
              resetPolling();
              setSignatureOpen(true);
            }}
          >
            {reOpenEsigningMessage}
          </Button>
        </Box>
      </>}

      {/* Show the form */}
      {showForm && (
        <>
          {shouldSaveInProgressForms && (
            <Box
              sx={{
                zIndex: 2,
                position: 'fixed',
                bottom: isSmall ? 62 : 24,
                right: isSmall ? 7 : 24,
                bgcolor: 'transparent',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                p: 0,
                m: 0,
              }}
            >
              {formSaveStatus === 'idle' && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip title="Save form" placement="top" PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] }}>
                    <IconButton onClick={handleFormSave}>
                      <Icon className="material-icons-filled" style={{ color: secondaryColor, fontSize: 15 }}>save</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {formSaveStatus === 'saving' && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={18} thickness={5} style={{ color: secondaryColor }} />
                </Box>
              )}
              {formSaveStatus === 'saved' && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip title="Form saved" placement="top" PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -6] } }] }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      }}
                    >
                      <Icon className="material-icons-outlined" style={{ color: 'white', fontSize: 20 }}>check</Icon>
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ mb: 4 }}>
            <Form
              submission={submission}
              formReady={() => {
                isFormReady.current = true
              }}
              onChange={handleFormChange}
              form={finalForm ?? form?.Config}
              options={{ noAlerts: true }}
              onSubmit={(event: any) => onSubmit(event, form)}
            />
          </Box>
        </>
      )}

      {/* Show loading the form. */}
      {showPrimaryFormLoadingSpinner && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Task is complete message */}
      {showCompleteMessage ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          Thank you for completing your {TaskText}!
        </Alert>
      ) : (
        ""
      )}

      {/* Task is canceled message */}
      {showCancelledMessage ? (
        <Alert icon={<CancelOutlinedIcon />} severity="error" sx={{ mb: 3 }}>
          This {TaskText} has been canceled
        </Alert>
      ) : (
        ""
      )}


      {/* Open dialog for Tasks that might have uploads associated with them for downloading :) */}
      {showLinkToDownloadForm || showLinkToDownloadSigningDocs || showLinkToDownloadUploadTaskDocs ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 1,
          }}
        >
          <DownloadUploadsComponent
            button={
              <Button
                variant="contained"
                sx={{
                  borderRadius: "40px",
                  minWidth: "250px",
                  color: "white",
                }}
              >
                {isWiringInstructionsTask ?
                  (WiringInstructionsTaskCompleteButtonText || `Download ${toTitleCase(WiringInstructionsText)}`) :
                  `Download ${toTitleCase(TaskText)} Documents`
                }
              </Button>
            }
          />
        </Box>
      ) : (
        ""
      )}

      {
        (isAccountVerificationTask && !notification.IsComplete) ? (
          <AccountVerificationTask />
        ) : <></>
      }

      {/* Form is complete, signing incomplete */}
      {showSigningIncompleteDetails ? (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {additionalSigners.length ? (
            <>

              {userCompletedSigning ?
                <>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    You have completed your part of this e-signing!
                  </Typography>
                  <Typography variant="body2">
                    Once the parties listed below have completed e-signing, the signed document
                    will be available for you to download here, a copy will be
                    sent to us, and this {TaskText} will be completed.
                  </Typography>
                </> :
                <>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    If you haven't completed your e-signing, please click "
                    {reOpenEsigningMessage}" below.
                  </Typography>
                  <Typography variant="body2">
                    Once you and the parties listed below have completed
                    e-signing, the signed document will be available for you to
                    download here, a copy will be sent to us, and this {TaskText} will
                    be completed.
                  </Typography>
                </>
              }

              <Typography sx={{ fontWeight: "bold", mt: 2 }}>
                {getAdditionalSignersText()}
              </Typography>
              <List>
                {additionalSigners.map((signer, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      sx={{ wordBreak: "break-word" }}
                      primaryTypographyProps={{ fontSize: "default" }}
                      primary={`${signer.firstName} ${signer.middleName || ""
                      } ${signer.lastName}`}
                      secondary={signer.email}
                      secondaryTypographyProps={{
                        variant: "light",
                        fontSize: "small",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              {userCompletedSigning ? (
                <>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    You have completed your part of this e-signing!
                  </Typography>
                  <Typography variant="body2">
                    Once all parties have completed their e-signing, the signed document
                    will be available for you to download here, a copy will be
                    sent to us, and this {TaskText} will be completed.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    If you haven't completed your e-signing, please click "
                    {reOpenEsigningMessage}" below.
                  </Typography>
                  <Typography variant="body2">
                    Once all parties have completed their e-signing, the signed document
                    will be available for you to download here, a copy will be
                    sent to us, and this {TaskText} will be completed.
                  </Typography>
                </>
              )}
            </>
          )}

          <Button
            variant={isSigningTask ? "contained" : "outlined"}
            sx={{
              minWidth: "250px",
              alignSelf: "center",
              mt: 4,
              borderRadius: "40px",
            }}
            onClick={() => {
              resetPolling();
              setSignatureOpen(true);
            }}
          >
            {reOpenEsigningMessage}
          </Button>
        </Box>
      ) : (
        ""
      )}

      {/* Form is complete, Show signing dialog */}
      {showSigningDialog && notification ? (
        <PortalSignature
          form={form}
          order={globalOrderData.Order}
          notification={notification}
          open={signatureOpen}
          setOpen={setSignatureOpen}
          onSigningClose={onSigningClose}
          setUserCompletedSigning={setUserCompletedSigning}
          userCompletedSigning={userCompletedSigning}
        />
      ) : (
        ""
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >

        {/* User complete task */}
        {showUploadButton && (
          <LoadingButton
            loading={userCompleteLoading}
            variant="contained"
            sx={{
              borderRadius: "40px",
              minWidth: "250px",
            }}
            onClick={handleUploadClick}
          >
            Upload documents{uploadInput}
          </LoadingButton>
        )}


        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Show Id check */}
          {showIdCheckButton && (
            <>
              <LoadingButton
                loading={getIdCheckUrlLoading}
                variant="contained"
                sx={{
                  borderRadius: "40px",
                  minWidth: "250px",
                }}
                onClick={handleIdCheckClick}
              >
                Verify your identity
              </LoadingButton>
            </>
          )}
          {idCheckUrl && !isPolling && (
            <Button
              variant="outlined"
              sx={{
                borderRadius: "40px",
                minWidth: "250px",
              }}
              onClick={() => { setIsPolling(true) }}
            >
              I've completed verification
            </Button>
          )}
        </Box>

        {/* Show Id check link for blocked popups */}
        {idCheckUrl && (
          <Box sx={{ mt: 2 }}>
            If a new window did not open you can allow pop-ups or&nbsp;
            <a href={idCheckUrl} target='_blank' rel='noreferrer'>
              click here to verify your identity in a new window.
            </a>
          </Box>
        )}

        {isEarnestMoneyTask && <EarnestMoneyTask
          orderKey={orderKey}
          notificationId={notification?.NotificationId}
        />}

        {isCashToCloseTask && <CashToCloseTask />}

        {/* User complete task */}
        {showUserCompleteButton && (
          <LoadingButton
            loading={userCompleteLoading}
            variant="text"
            sx={{
              borderRadius: "40px",
              minWidth: "250px",
              mt: 1,
            }}
            onClick={() => setUserCompleteOpen(true)}
          >
            Complete {TaskText}
          </LoadingButton>
        )}


        {/* User cancel task */}
        {showUserCancelButton && (
          <LoadingButton
            loading={userCancelLoading}
            variant="text"
            sx={{
              borderRadius: "40px",
              minWidth: "250px",
              color: "primary.main",
              mt: 1,
            }}
            onClick={() => setUserCancelOpen(true)}
          >
            Cancel this {TaskText}
          </LoadingButton>
        )}


        {/* User reset task */}
        {showUserResetButton && (
          <LoadingButton
            loading={userResetLoading || (formRetrieveFetching)}
            variant="text"
            sx={{
              borderRadius: "40px",
              minWidth: "250px",
              color: "primary.main",
              mt: 1,
            }}
            onClick={() => setUserResetOpen(true)}
          >
            Restart this {TaskText}
          </LoadingButton>
        )}
      </Box>


      {/* Additional navigation. Next task, Next Message, etc. */}
      {showNotificationPagination ? (
        <NotificationPagination
          isTasks={true}
          currentNotificationId={notification?.NotificationId}
        />
      ) : (
        ""
      )}

      <CompleteTaskDialog
        open={userCompleteOpen}
        setUserCompleteOpen={setUserCompleteOpen}
        onUserCompleteTask={onUserCompleteTask}
      />

      <CancelTaskDialog
        open={userCancelOpen}
        setUserCancelOpen={setUserCancelOpen}
        onUserCancelTask={onUserCancelTask}
        isSigningTask={isSigningTask}
      />

      <ResetTaskDialog
        open={userResetOpen}
        setUserResetOpen={setUserResetOpen}
        onUserResetTask={onUserResetTask}
      />

      {/* Form submission error dialog, to allow for user retries without losing data */}
      <FormSubmitErrorDialog
        open={formSubmitErrorOpen}
        setFormSubmitErrorOpen={setFormSubmitErrorOpen}
        mutateFormSubmit={mutateFormSubmit}
        formData={formData}
      />

      {/* Dialog for upload task */}
      <UploadDialog
        open={uploadTaskDialogOpen}
        onClose={onUploadTaskDialogClose}
        fileUploadBody={fileUploadBody}
        fileUploadActions={fileUploadActions}
        isComplete={isComplete}
      />


      {pollNotificationsDialog}
    </>
  );
}
